import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faUsers,
  faClock,
  faHouse,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons";
import { DefaultDateSmall } from "../components/DefaultImports";

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 50px;
  overflow: hidden;
  border-radius: 25px;
  margin-bottom: 10px;
`;

const Step = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ active, completed, $mainColor, $widgetDark }) => {
    if (active || completed) return $mainColor;
    return $widgetDark ? "rgba(255, 255, 255, 0.12)" : "#f2f2f2";
  }};
  color: ${({ active, completed, $widgetDark }) => {
    if (active || completed) return "white";
    return $widgetDark ? "rgba(246, 241, 232, 0.7)" : "#a0a0a0";
  }};
  font-weight: 500;
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  position: relative;
  padding: 4px 6px;
  text-align: center;
  font-size: 11px;
  transition: all 0.3s ease;
  min-width: 0;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: -8px;
    top: 0;
    width: 0;
    height: 0;
    border-top: 25px solid transparent;
    border-bottom: 25px solid transparent;
    border-left: 8px solid
      ${({ active, completed, $mainColor, $widgetDark }) => {
        if (active || completed) return $mainColor;
        return $widgetDark ? "rgba(255, 255, 255, 0.12)" : "#f2f2f2";
      }};
    z-index: 1;
  }

  &:first-child {
    border-top-left-radius: 25px;
    border-bottom-left-radius: 25px;
  }

  &:last-child {
    border-top-right-radius: 25px;
    border-bottom-right-radius: 25px;
  }

  .step-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
  }

  .step-icon {
    font-size: 13px;
    margin-bottom: 2px;
  }

  .step-text {
    font-size: 11px;
    line-height: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 90%;
    white-space: nowrap;
  }

  .step-subtext {
    font-size: 10px;
    line-height: 1;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 90%;
  }

  @media (max-width: 420px) {
    padding: 3px 3px;

    .step-icon {
      font-size: 11px;
      margin-bottom: 1px;
    }

    .step-text {
      font-size: 9px;
    }

    .step-subtext {
      font-size: 8px;
    }
  }
`;

const StepperComponent = ({
  startDate = "",
  numPeople = "",
  time = "",
  placeOfTable = "",
  changeStep,
  currentStep,
  reservation_or_waiting_list,
  timezone,
  from_restaurant,
  mainColor = "#EE754C",
  widgetDark = false,
}) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    { id: "date", icon: faCalendarAlt, label: t("ui.components.stepBar.date") },
    { id: "people", icon: faUsers, label: t("ui.components.stepBar.people") },
    {
      id: "timeAndRoom",
      icon: reservation_or_waiting_list === "reservation" ? faClock : faHouse,
      label: t("ui.components.stepBar.timeAndRoom"),
    },
    { id: "information", icon: faNoteSticky, label: t("ui.components.stepBar.information") },
  ];

  useEffect(() => {
    setActiveStep(currentStep);
  }, [currentStep]);

  const getDateInTimezone = () => {
    const now = new Date();
    const dateInTimezone = new Date(
      now.toLocaleString("en-US", { timeZone: timezone }),
    );
    dateInTimezone.setHours(0, 0, 0, 0);
    return dateInTimezone;
  };

  const getLabelForDate = () => {
    const today = getDateInTimezone();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const date = new Date(startDate);
    date.setHours(0, 0, 0, 0);

    if (date.getTime() === today.getTime()) return t("ui.components.stepBar.today");
    if (date.getTime() === tomorrow.getTime()) return t("ui.components.stepBar.tomorrow");
    return <DefaultDateSmall date={date} />;
  };

  const getStepText = (step) => {
    if (step.id === "date" && startDate) return getLabelForDate();
    if (step.id === "people" && numPeople)
      return t("ui.components.stepBar.personCount", { count: Number(numPeople) });
    if (step.id === "timeAndRoom" && time) return time;
    return step.label;
  };

  const getStepSubText = () => {
    const cleanPlace =
      placeOfTable.endsWith("/f") || placeOfTable.endsWith("/b")
        ? placeOfTable.slice(0, -2)
        : placeOfTable;

    return cleanPlace.length > 18
      ? `${cleanPlace.substring(0, 18)}...`
      : cleanPlace;
  };

  const handleClick = (index) => {
    if (index <= activeStep || from_restaurant) {
      if (!(reservation_or_waiting_list === "waiting_list" && index === 0)) {
        setActiveStep(index);
        changeStep(index);
      }
    }
  };

  return (
    <Container>
      {steps.map((step, index) => (
        <Step
          key={index}
          active={index === activeStep}
          completed={index < activeStep}
          clickable={index <= activeStep || from_restaurant}
          $mainColor={mainColor}
          $widgetDark={widgetDark}
          onClick={() => handleClick(index)}
        >
          <div className="step-content">
            <FontAwesomeIcon icon={step.icon} className="step-icon" />
            <span className="step-text">{getStepText(step)}</span>
            {step.id === "timeAndRoom" && time && (
              <span className="step-subtext" title={placeOfTable}>
                {getStepSubText()}
              </span>
            )}
          </div>
        </Step>
      ))}
    </Container>
  );
};

export default StepperComponent;
